import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FinanceBreadcrumbs from './components/FinanceBreadcrumbs';
import ScopeOverview from './components/Overview/ScopeOverview';
import NextMonthCard from './components/Overview/NextMonthCard';
import GroupsTable from './components/Overview/GroupsTable';
import ExportMenu from './components/Overview/ExportMenu';
import { scopeGate } from './components/Overview/scopeGate';
import { forMonth } from './components/Overview/monthText';
import { OverviewScope } from './components/Overview/scope';
import { useFinanceKindergarten } from './useFinanceKindergarten';
import { useFinanceMonth } from './useFinanceMonth';
import { useFinanceSummary } from './useFinanceSummary';
import { useFinanceComposition, useFinanceToday } from './useFinanceToday';
import { FINANCE } from '../../common/constants/routes.constants';
import { FeatureCode } from '../../common/enums/common.enum';
import { useKindergartenFeatures } from '../../utilities/hooks/useKindergartenFeatures';

/** One location, laid out as the overview, broken down by the groups inside it. */
const FinanceLocationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locationId = '' } = useParams<{ locationId: string }>();
  const { kindergartenId, kindergartenName } = useFinanceKindergarten();
  const [month, setMonth] = useFinanceMonth();
  const { isEnabled, isLoading: isLoadingFeatures } = useKindergartenFeatures(kindergartenId || undefined);
  const enabled = !!kindergartenId && !isLoadingFeatures && isEnabled(FeatureCode.INVOICING);
  const { summary, trend, isLoading, failed } = useFinanceSummary({
    kindergartenId: enabled ? kindergartenId : '',
    month,
    locationId,
    trendMonths: 12
  });
  const { todo, buckets, nextMonth } = useFinanceToday({ kindergartenId, enabled, locationId });
  const composition = useFinanceComposition({ kindergartenId, enabled, locationId, month });

  const name = summary?.scope.locationName ?? '';
  const groups = summary?.breakdown.filter(row => row.id) ?? [];
  const children = groups.reduce((sum, row) => sum + row.childCount, 0);
  const scope: OverviewScope = { kind: 'location', locationId, name, groupIds: groups.map(row => row.id as string) };

  const gate = scopeGate(t, {
    kindergartenId,
    loading: isLoadingFeatures || (isLoading && !summary),
    invoicingOn: isEnabled(FeatureCode.INVOICING),
    failed: failed || !summary
  });

  return (
    <Box sx={{ p: 7 }}>
      <FinanceBreadcrumbs
        kindergartenId={kindergartenId}
        locationId={locationId}
        fallbackNames={{ location: summary?.scope.locationName }}
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h2">{name || t('common.location')}</Typography>
          {summary && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>
              {t('finance.scope.groups', { count: groups.length })} · {t('finance.scope.children', { count: children })}
            </Typography>
          )}
        </Box>
        {enabled && <ExportMenu kindergartenId={kindergartenId} kindergartenName={kindergartenName ?? ''} month={month} scope={scope} />}
      </Stack>
      {gate ??
        (summary && (
          <ScopeOverview
            kindergartenId={kindergartenId}
            scope={scope}
            month={month}
            onMonthChange={setMonth}
            summary={summary}
            trend={trend}
            todo={todo}
            buckets={buckets}
            composition={composition}
            aside={<NextMonthCard data={nextMonth} kindergartenId={kindergartenId} scope={scope} />}
            tableTitle={t('finance.overview.byGroup', { month: forMonth(t, month) })}
            table={
              <GroupsTable
                rows={summary.breakdown}
                totals={summary.totals}
                totalLabel={t('finance.scope.total', { name })}
                onRowClick={row =>
                  navigate(`${FINANCE}/location/${locationId}/group/${row.id}?kindergartenId=${kindergartenId}&month=${month}`)
                }
              />
            }
          />
        ))}
    </Box>
  );
};

export default FinanceLocationPage;
